import React, { Suspense } from 'react';
import { Helmet } from 'react-helmet-async';
import { Outlet, useNavigate } from 'react-router-dom';

import { NotFoundPage, PrivacyToggleProvider } from '@eluve/blocks';
import {
  Content,
  Header,
  Icon,
  Layout,
  LoadingIndicator,
  Main,
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarHeaderTrigger,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuNavButton,
  SidebarTrigger,
  cn,
  useSidebar,
} from '@eluve/components';
import { useSession } from '@eluve/session-helpers';

import { CommandPaletteHost } from './command-palette/CommandPaletteHost';
import {
  AdminNavItem,
  adminNavGroups,
} from './command-palette/adminNavRegistry';
import { AdminBreadcrumbs } from './components/AdminBreadcrumbs';
import { AdminUserDropdownMenu } from './components/AdminUserDropdownMenu';

const AdminNavRow: React.FC<AdminNavItem & { isActive?: boolean }> = ({
  to,
  label,
  icon,
  isActive,
}) => (
  <SidebarMenuItem>
    <SidebarMenuNavButton to={to} tooltip={label} isActive={isActive}>
      <Icon name={icon} size="auto" />
      <span>{label}</span>
    </SidebarMenuNavButton>
  </SidebarMenuItem>
);

const AdminBrandHeader: React.FC = () => {
  const { canBeExpanded, isMobile, state } = useSidebar();
  const navigate = useNavigate();
  const isCollapsed = state !== 'expanded' && !isMobile;

  return (
    <div
      className={cn(
        'flex h-14 items-start justify-between gap-2 transition-[height] duration-200 ease-linear',
        isCollapsed && 'flex-col pb-0',
        isCollapsed && canBeExpanded && 'h-28',
      )}
    >
      <button
        type="button"
        onClick={() => navigate('/admin')}
        className={cn(
          'my-2 flex h-10 shrink-0 cursor-pointer items-center gap-2 rounded-lg px-2 transition-[background,padding] duration-200 ease-linear',
          isCollapsed && 'bg-sidebar-primary px-2',
        )}
      >
        <Icon
          name="ShieldCheck"
          size="auto"
          className={cn(
            'size-6 shrink-0',
            isCollapsed && 'text-sidebar-primary-foreground',
          )}
        />
        <span
          className={cn(
            'overflow-hidden text-sm font-semibold whitespace-nowrap transition-[width,opacity] duration-200 ease-linear',
            isCollapsed ? 'w-0 opacity-0' : 'w-auto opacity-100',
          )}
        >
          Eluve Admin
        </span>
      </button>
      {canBeExpanded && <SidebarHeaderTrigger />}
    </div>
  );
};

export const AdminLayout: React.FC = () => {
  const session = useSession();

  if (!session.isEluveAdmin) {
    return <NotFoundPage />;
  }

  return (
    <>
      <Helmet>
        <title>Admin | Eluve</title>
      </Helmet>
      <Layout>
        <Sidebar variant="inset">
          <SidebarHeader>
            <AdminBrandHeader />
          </SidebarHeader>
          <SidebarContent>
            {adminNavGroups.map((group, groupIdx) => (
              <SidebarGroup key={groupIdx}>
                <SidebarGroupContent>
                  <SidebarMenu>
                    {group.map((item) => (
                      <AdminNavRow key={item.to} {...item} />
                    ))}
                  </SidebarMenu>
                </SidebarGroupContent>
              </SidebarGroup>
            ))}
          </SidebarContent>
          <SidebarFooter>
            <SidebarMenu>
              <AdminNavRow
                to="/"
                label="Return to App"
                icon="LogOut"
                isActive={false}
              />
            </SidebarMenu>
            <AdminUserDropdownMenu />
          </SidebarFooter>
        </Sidebar>
        <Main>
          <Header>
            <SidebarTrigger />
            <AdminBreadcrumbs />
          </Header>
          <Content padded>
            <PrivacyToggleProvider>
              <Suspense fallback={<LoadingIndicator size="xl" />}>
                <Outlet />
              </Suspense>
            </PrivacyToggleProvider>
          </Content>
        </Main>
        <CommandPaletteHost />
      </Layout>
    </>
  );
};
